import { useState, useEffect, useRef } from 'react'

import { usePageEditorContext } from '../../contexts/page-editor.context'
import { ReportBlock } from '../../blocks/report-block'
import { selectComponentService } from '../../../common/services/select-component.service'
import { NotificationManager } from '../../../common/components/Notifications'
import { reportDataSetService } from '../report-data-set.service'
import { DatasetFieldsEditor, DatasetEmptyState } from '../dataset-fields-editor'
import { SUBGROUP_COLUMN } from '../report-data-set.constants'
import { BlockEditButtonGroup } from '../block-edit-button-group'
import { tableEditService } from './table-edit.service'

const subgroupColumns = [SUBGROUP_COLUMN.PAGE_FILTER, SUBGROUP_COLUMN.PRIMARY, SUBGROUP_COLUMN.FILTERS, SUBGROUP_COLUMN.SLIDER]

export const DataTableEditInline = function () {
  const pageEditorContext = usePageEditorContext() || {}
  const currentEdit = pageEditorContext.currentEdit || {}
  const block = currentEdit.block || {}
  block.props = block.props || {}

  const dataSetOptions = useRef(reportDataSetService.getDataSetOptions(pageEditorContext.page, 'table', { includeUntyped: false }))
  const [editMode, setEditMode] = useState(true)
  const [dataKeyStatus, setDataKeyStatus] = useState(getDataKeyStatus())

  useEffect(() => {
    if (block.dataKey) {
      initializeConfigs(block)
    }
  }, [])

  useEffect(() => {
    setDataKeyStatus(getDataKeyStatus())
  }, [block.dataKey, block.props.parentControlBar, block.props.parentBlock])

  async function initializeConfigs (block) {
    const blockConfig = await validateAndCompleteConfig(block)
    if (blockConfig) {
      pageEditorContext.updateCurrentEdit({ block: blockConfig })
    }
  }

  async function validateAndCompleteConfig (block) {
    let blockConfig
    try {
      pageEditorContext.updateCurrentEdit({ isLoading: true })
      const result = await tableEditService.validateAndCompleteConfig(block, pageEditorContext.page.parent.id)
      blockConfig = result.data.blockConfig
    } catch (e) {
      NotificationManager.create({ type: 'error', timeOut: 15000, message: 'There was a problem adding your chart. Please contact an admin.' })
    } finally {
      pageEditorContext.updateCurrentEdit({ isLoading: false })
    }
    return blockConfig
  }

  function getDataKeyStatus () {
    return {
      isSelected: block.dataKey !== undefined,
      isValid: selectComponentService.isKnownField(dataSetOptions.current, block.dataKey)
    }
  }

  async function handleDataSetChange ({ value }) {
    const partialBlockConfig = { ...block }
    if (partialBlockConfig.dataKey !== value) {
      // Reset the block props when switching datasets
      partialBlockConfig.props = {}
    }

    partialBlockConfig.dataKey = value
    const blockConfig = await validateAndCompleteConfig(partialBlockConfig)
    updateCurrentEdit({ block: blockConfig })
  }

  function updateCurrentEdit (currentEdit) {
    // Ensure any updates to the block result in a new object
    if (currentEdit.block) {
      currentEdit.block = { ...currentEdit.block }
    }

    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit(currentEdit)
  }

  if (!dataKeyStatus.isSelected) {
    return <DatasetEmptyState block={block} dataSetOptions={dataSetOptions.current} isDatasetKeyValid={dataKeyStatus.isValid} handleDataSetChange={handleDataSetChange} />
  }

  return (
    <div className="report-editor-inline">
      <div className={editMode ? 'button-row-edit-mode' : 'button-row'}>
        {block.dataKey !== undefined && (
          <BlockEditButtonGroup isInEditMode={editMode} onModeToggle={setEditMode} canPreview={true} />
        )}
      </div>
      {editMode
        ? (
          <DatasetFieldsEditor
            block={block}
            datasetOptions={dataSetOptions.current}
            isDatasetKeyValid={dataKeyStatus.isValid}
            handleDataSetChange={handleDataSetChange}
            subgroupColumns={subgroupColumns}
          />
        )
        // Preview
        : (
          <ReportBlock
            blockKey={block.key}
            report={pageEditorContext.page}
            isPreviewMode={true}
            {...block}
          />
        )
      }
    </div>
  )
}
